  import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
  import { supabase } from '@/lib/supabase'
  import { traducirError } from '@/lib/errores'
  import { toast } from 'sonner'
  import { useAuth } from '@/auth/AuthProvider'
  import type { Database } from '@/types/database'
  import { getPlanLimites, verificarLimiteTurnos, verificarLimiteEmpleados, verificarLimiteClientes, type ResultadoVerificacion } from '@/lib/planes'

  // ===== Tipos generados desde Supabase =====
  export type Cliente = Database['public']['Tables']['clientes']['Row']
  export type ClienteInsert = Database['public']['Tables']['clientes']['Insert']
  export type ClienteUpdate = Database['public']['Tables']['clientes']['Update']

  export type Servicio = Database['public']['Tables']['servicios']['Row']
  export type ServicioInsert = Database['public']['Tables']['servicios']['Insert']
  export type ServicioUpdate = Database['public']['Tables']['servicios']['Update']

  export type Empleado = Database['public']['Tables']['empleados']['Row']
  export type EmpleadoInsert = Database['public']['Tables']['empleados']['Insert']
  export type EmpleadoUpdate = Database['public']['Tables']['empleados']['Update']

  export type Categoria = Database['public']['Tables']['categorias']['Row']

  export type Local = Database['public']['Tables']['tenants']['Row'] & {
    config: Database['public']['Tables']['tenants']['Row']['config']
    suscripcion_vitalicia_gratis?: boolean
  }

  // ===== Horarios =====
  export type HorarioEmpleado = Database['public']['Tables']['horarios_empleado']['Row']

  // ===== Helpers =====
  export function nombreCliente(c: { nombre: string; apellido?: string | null; referencia?: string | null }) {
    const base = `${c.nombre} ${c.apellido ?? ''}`.trim()
    return c.referencia ? `${base} (${c.referencia})` : base
  }

  function useLista<T>(tabla: string, columnas: string, orden: string, soloActivos = false) {
    const { tenantId } = useAuth()
    return useQuery({
      queryKey: [tabla, tenantId, soloActivos],
      queryFn: async () => {
        const base = supabase.from(tabla).select(columnas)
        const { data, error } = await (soloActivos ? base.eq('activo', true) : base).order(orden)
        if (error) throw error
        return data as unknown as T[]
      },
    })
  }

  export const useClientes = () =>
    useLista<Cliente>('clientes', 'id, nombre, apellido, referencia, whatsapp, notas, acepta_recordatorios', 'nombre')

  export const useServicios = (soloActivos = true) =>
    useLista<Servicio>('servicios', 'id, nombre, categoria_id, duracion_min, precio, activo', 'nombre', soloActivos)

  export const useEmpleados = (soloActivos = true) =>
    useLista<Empleado>('empleados', 'id, nombre, color, activo, user_id', 'nombre', soloActivos)

  export const useCategorias = () => useLista<Categoria>('categorias', 'id, nombre', 'nombre')

  export function useHorariosEmpleado(empleadoId?: string) {
    const { tenantId } = useAuth()
    return useQuery({
      queryKey: ['horarios_empleado', empleadoId, tenantId],
      enabled: !!tenantId,
      queryFn: async () => {
        let query = supabase
          .from('horarios_empleado')
          .select('id, empleado_id, dia_semana, desde, hasta')
          .eq('tenant_id', tenantId)
        if (empleadoId) query = query.eq('empleado_id', empleadoId)
        const { data, error } = await query.order('empleado_id').order('dia_semana')
        if (error) throw error
        return data as HorarioEmpleado[]
      },
    })
  }

  export function useLocal() {
    const { tenantId } = useAuth()
    return useQuery({
      queryKey: ['tenant', tenantId],
      enabled: !!tenantId,
      queryFn: async () => {
        const { data, error } = await supabase
          .from('tenants')
          .select('nombre, config, plan, estado_suscripcion, trial_hasta, suscripcion_vitalicia_gratis')
          .eq('id', tenantId!)
          .single()
        if (error) throw error
        return data as Local
      },
    })
  }

  // ===== Mutations tipadas =====
  export function useCrearCliente() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: async (nuevo: ClienteInsert) => {
        const { data, error } = await supabase.from('clientes').insert(nuevo).select().single()
        if (error) throw error
        return data
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: ['clientes'] }),
      onError: (e) => toast.error(traducirError(e, 'database')),
    })
  }

  export function useActualizarCliente() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: async (c: ClienteUpdate & { id: string }) => {
        const { id, ...rest } = c
        const { error } = await supabase.from('clientes').update(rest).eq('id', id)
        if (error) throw error
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: ['clientes'] }),
      onError: (e) => toast.error(traducirError(e, 'database')),
    })
  }

  export function useBorrarCliente() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: async (id: string) => {
        const { error } = await supabase.from('clientes').delete().eq('id', id)
        if (error) throw error
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: ['clientes'] }),
      onError: (e) => toast.error(traducirError(e, 'database')),
    })
  }

  export function useCrearServicio() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: async (nuevo: ServicioInsert) => {
        const { data, error } = await supabase.from('servicios').insert(nuevo).select().single()
        if (error) throw error
        return data
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: ['servicios'] }),
      onError: (e) => toast.error(traducirError(e, 'database')),
    })
  }

  export function useActualizarServicio() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: async (s: ServicioUpdate & { id: string }) => {
        const { id, ...rest } = s
        const { error } = await supabase.from('servicios').update(rest).eq('id', id)
        if (error) throw error
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: ['servicios'] }),
      onError: (e) => toast.error(traducirError(e, 'database')),
    })
  }

  export function useCrearEmpleado() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: async (nuevo: EmpleadoInsert) => {
        const { data, error } = await supabase.from('empleados').insert(nuevo).select().single()
        if (error) throw error
        return data
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: ['empleados'] }),
      onError: (e) => toast.error(traducirError(e, 'database')),
    })
  }

  export function useActualizarEmpleado() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: async (e: EmpleadoUpdate & { id: string }) => {
        const { id, ...rest } = e
        const { error } = await supabase.from('empleados').update(rest).eq('id', id)
        if (error) throw error
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: ['empleados'] }),
      onError: (e) => toast.error(traducirError(e, 'database')),
    })
  }

// ===== Historial de turnos por cliente =====
export interface TurnoHistorial {
  id: string
  inicio: string
  estado: string
  notas: string | null
  servicio: { nombre: string; duracion_min: number }
  empleado: { nombre: string; color: string }
}

export function useHistorialCliente(clienteId: string | null) {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['historial-cliente', clienteId, tenantId],
    enabled: !!clienteId && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('turnos')
        .select(`
          id,
          inicio,
          estado,
          notas,
          servicio:servicios!inner(nombre, duracion_min),
          empleado:empleados!inner(nombre, color)
        `)
        .eq('cliente_id', clienteId)
        .eq('tenant_id', tenantId)
        .order('inicio', { ascending: false })
        .limit(10)
      if (error) throw error
      return data as unknown as TurnoHistorial[]
    },
  })
}

// ===== Recordatorios pendientes (Phase 1.5) =====
export interface RecordatorioPendiente {
  id: string
  turno_id: string
  cliente_id: string
  whatsapp_cliente: string
  mensaje: string
  link_wa: string
  tipo: string
  estado: string
  creado_en: string
}

export function useRecordatoriosPendientes(tipo?: string) {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['recordatorios-pendientes', tenantId, tipo],
    enabled: !!tenantId,
    queryFn: async () => {
      let query = supabase
        .from('recordatorios_pendientes')
        .select('*')
        .eq('tenant_id', tenantId!)
        .eq('estado', 'pendiente')
        .order('creado_en')
      if (tipo) query = query.eq('tipo', tipo)
      const { data, error } = await query
      if (error) throw error
      return data as unknown as RecordatorioPendiente[]
    },
  })
}

export function useEnviarRecordatorio() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('recordatorios_pendientes')
        .update({ estado: 'enviado', enviado_en: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recordatorios-pendientes'] })
    },
  })
}

export function useEnviarTodosRecordatorios() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from('recordatorios_pendientes')
        .update({ estado: 'enviado', enviado_en: new Date().toISOString() })
        .in('id', ids)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recordatorios-pendientes'] })
    },
  })
}

// ===== Plan Free Limits =====

export function usePlanLimites() {
  const { tenantId } = useAuth()
  const { data: local } = useLocal()

  const plan = local?.plan || 'free'
  const vitaliciaGratis = local?.suscripcion_vitalicia_gratis || false
  const limites = getPlanLimites(plan, { vitaliciaGratis })

  return { plan, limites, tenantId, vitaliciaGratis }
}

export function useVerificarLimiteTurnos() {
  const { tenantId } = useAuth()
  const { data: local } = useLocal()
  const plan = local?.plan || 'free'
  const vitaliciaGratis = local?.suscripcion_vitalicia_gratis || false

  return async (): Promise<ResultadoVerificacion> => {
    if (!tenantId) return { permitido: true }
    return verificarLimiteTurnos(supabase, tenantId, plan, { vitaliciaGratis })
  }
}

export function useVerificarLimiteEmpleados() {
  const { tenantId } = useAuth()
  const { data: local } = useLocal()
  const plan = local?.plan || 'free'
  const vitaliciaGratis = local?.suscripcion_vitalicia_gratis || false

  return async (): Promise<ResultadoVerificacion> => {
    if (!tenantId) return { permitido: true }
    return verificarLimiteEmpleados(supabase, tenantId, plan, { vitaliciaGratis })
  }
}

export function useVerificarLimiteClientes() {
  const { tenantId } = useAuth()
  const { data: local } = useLocal()
  const plan = local?.plan || 'free'
  const vitaliciaGratis = local?.suscripcion_vitalicia_gratis || false

  return async (): Promise<ResultadoVerificacion> => {
    if (!tenantId) return { permitido: true }
    return verificarLimiteClientes(supabase, tenantId, plan, { vitaliciaGratis })
  }
}

// Hook combinado para usar en componentes
export function useLimitesPlan() {
  const { plan, limites, tenantId, vitaliciaGratis } = usePlanLimites()
  const verificarTurnos = useVerificarLimiteTurnos()
  const verificarEmpleados = useVerificarLimiteEmpleados()
  const verificarClientes = useVerificarLimiteClientes()

  // Si tiene vitalicia gratis, se comporta como enterprise (ilimitado)
  const esEnterprise = plan === 'enterprise' || vitaliciaGratis
  const esPro = plan === 'pro' || vitaliciaGratis
  const esFree = plan === 'free' && !vitaliciaGratis

  return {
    plan,
    limites,
    tenantId,
    vitaliciaGratis,
    esFree,
    esPro,
    esEnterprise,
    verificarTurnos,
    verificarEmpleados,
    verificarClientes,
  }
}

// ===== Push Notifications (PWA) =====
export { usePushNotifications } from './usePushNotifications'
export type { PushSubscriptionData } from './usePushNotifications'

// ==========================================
// ===== INVENTARIO =====
// ==========================================

// Tipos locales (hasta regenerar database.ts con Supabase)
export interface CategoriaInsumo {
  id: string
  tenant_id: string
  nombre: string
  descripcion: string | null
  color: string
  orden: number
  created_at: string
  updated_at: string
}

export interface CategoriaInsumoInsert {
  tenant_id: string
  nombre: string
  descripcion?: string | null
  color?: string
  orden?: number
}

export interface CategoriaInsumoUpdate {
  nombre?: string
  descripcion?: string | null
  color?: string
  orden?: number
}

export interface Proveedor {
  id: string
  tenant_id: string
  nombre: string
  cuit: string | null
  email: string | null
  telefono: string | null
  direccion: string | null
  dias_entrega: number
  cuenta_bancaria: string | null
  notas: string | null
  activo: boolean
  created_at: string
  updated_at: string
}

export interface ProveedorInsert {
  tenant_id: string
  nombre: string
  cuit?: string | null
  email?: string | null
  telefono?: string | null
  direccion?: string | null
  dias_entrega?: number
  cuenta_bancaria?: string | null
  notas?: string | null
  activo?: boolean
}

export interface ProveedorUpdate {
  nombre?: string
  cuit?: string | null
  email?: string | null
  telefono?: string | null
  direccion?: string | null
  dias_entrega?: number
  cuenta_bancaria?: string | null
  notas?: string | null
  activo?: boolean
}

export interface Insumo {
  id: string
  tenant_id: string
  categoria_id: string | null
  proveedor_id: string | null
  nombre: string
  descripcion: string | null
  unidad: string
  stock_actual: number
  stock_minimo: number
  costo_unitario: number
  costo_promedio: number
  activo: boolean
  created_at: string
  updated_at: string
  // Relaciones (opcionales, para joins)
  categoria?: CategoriaInsumo | null
  proveedor?: Proveedor | null
}

export interface InsumoInsert {
  tenant_id: string
  categoria_id?: string | null
  proveedor_id?: string | null
  nombre: string
  descripcion?: string | null
  unidad?: string
  stock_actual?: number
  stock_minimo?: number
  costo_unitario?: number
  costo_promedio?: number
  activo?: boolean
}

export interface InsumoUpdate {
  categoria_id?: string | null
  proveedor_id?: string | null
  nombre?: string
  descripcion?: string | null
  unidad?: string
  stock_actual?: number
  stock_minimo?: number
  costo_unitario?: number
  costo_promedio?: number
  activo?: boolean
}

export interface MovimientoStock {
  id: string
  tenant_id: string
  insumo_id: string
  tipo: 'entrada' | 'salida' | 'ajuste' | 'merma'
  cantidad: number
  costo_unitario: number | null
  origen: string
  referencia_id: string | null
  usuario_id: string | null
  notas: string | null
  created_at: string
  // Relaciones
  insumo?: Insumo | null
}

export interface MovimientoStockInsert {
  tenant_id: string
  insumo_id: string
  tipo: 'entrada' | 'salida' | 'ajuste' | 'merma'
  cantidad: number
  costo_unitario?: number | null
  origen: string
  referencia_id?: string | null
  usuario_id?: string | null
  notas?: string | null
}

export interface OrdenCompra {
  id: string
  tenant_id: string
  proveedor_id: string
  numero: string
  estado: 'borrador' | 'enviada' | 'recibida_parcial' | 'recibida' | 'cancelada'
  fecha_orden: string
  fecha_esperada: string | null
  fecha_recibida: string | null
  subtotal: number
  impuestos: number
  total: number
  notas: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  // Relaciones
  proveedor?: Proveedor | null
  items?: OrdenCompraItem[]
}

export interface OrdenCompraInsert {
  tenant_id: string
  proveedor_id: string
  numero: string
  estado?: 'borrador' | 'enviada' | 'recibida_parcial' | 'recibida' | 'cancelada'
  fecha_orden?: string
  fecha_esperada?: string | null
  subtotal?: number
  impuestos?: number
  total?: number
  notas?: string | null
  created_by?: string | null
}

export interface OrdenCompraUpdate {
  proveedor_id?: string
  estado?: 'borrador' | 'enviada' | 'recibida_parcial' | 'recibida' | 'cancelada'
  fecha_esperada?: string | null
  fecha_recibida?: string | null
  subtotal?: number
  impuestos?: number
  total?: number
  notas?: string | null
}

export interface OrdenCompraItem {
  id: string
  orden_compra_id: string
  insumo_id: string
  cantidad_solicitada: number
  cantidad_recibida: number
  costo_unitario_esperado: number
  costo_unitario_real: number | null
  subtotal_esperado: number
  subtotal_real: number
  notas: string | null
  insumo?: Insumo | null
}

export interface OrdenCompraItemInsert {
  orden_compra_id: string
  insumo_id: string
  cantidad_solicitada: number
  costo_unitario_esperado: number
  cantidad_recibida?: number
  costo_unitario_real?: number | null
  notas?: string | null
}

export interface IndiceInflacion {
  id: string
  tenant_id: string
  anio: number
  mes: number
  valor: number
  fuente: string
  created_at: string
}

export interface IndiceInflacionInsert {
  tenant_id: string
  anio: number
  mes: number
  valor: number
  fuente?: string
}

export interface ServicioInsumo {
  id: string
  tenant_id: string
  servicio_id: string
  insumo_id: string
  cantidad: number
  created_at: string
  insumo?: Insumo | null
}

export interface ServicioInsumoInsert {
  tenant_id: string
  servicio_id: string
  insumo_id: string
  cantidad: number
}

// Gasto mensual por categoría (resultado de función RPC)
export interface GastoMensualCategoria {
  categoria_id: string
  categoria_nombre: string
  gasto_nominal: number
  gasto_real: number
  inflacion_acum: number
}

// ===== Hooks: Categorías Insumo =====
export function useCategoriasInsumo() {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['categorias_insumo', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categorias_insumo')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('orden')
      if (error) throw error
      return data as CategoriaInsumo[]
    },
  })
}

export function useCrearCategoriaInsumo() {
  const qc = useQueryClient()
  const { tenantId } = useAuth()
  return useMutation({
    mutationFn: async (nuevo: CategoriaInsumoInsert) => {
      const { data, error } = await supabase
        .from('categorias_insumo')
        .insert({ ...nuevo, tenant_id: tenantId! })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categorias_insumo'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

export function useActualizarCategoriaInsumo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (c: CategoriaInsumoUpdate & { id: string }) => {
      const { id, ...rest } = c
      const { error } = await supabase.from('categorias_insumo').update(rest).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categorias_insumo'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

export function useBorrarCategoriaInsumo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('categorias_insumo').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categorias_insumo'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

// ===== Hooks: Proveedores =====
export function useProveedores(soloActivos = true) {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['proveedores', tenantId, soloActivos],
    enabled: !!tenantId,
    queryFn: async () => {
      let query = supabase.from('proveedores').select('*').eq('tenant_id', tenantId!)
      if (soloActivos) query = query.eq('activo', true)
      const { data, error } = await query.order('nombre')
      if (error) throw error
      return data as Proveedor[]
    },
  })
}

export function useCrearProveedor() {
  const qc = useQueryClient()
  const { tenantId } = useAuth()
  return useMutation({
    mutationFn: async (nuevo: ProveedorInsert) => {
      const { data, error } = await supabase
        .from('proveedores')
        .insert({ ...nuevo, tenant_id: tenantId! })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proveedores'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

export function useActualizarProveedor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (p: ProveedorUpdate & { id: string }) => {
      const { id, ...rest } = p
      const { error } = await supabase.from('proveedores').update(rest).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proveedores'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

// ===== Hooks: Insumos =====
export function useInsumos(soloActivos = true) {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['insumos', tenantId, soloActivos],
    enabled: !!tenantId,
    queryFn: async () => {
      // Fetch insumos without embedded relations
      let query = supabase
        .from('insumos')
        .select('*')
        .eq('tenant_id', tenantId!)
      if (soloActivos) query = query.eq('activo', true)
      const { data: insumos, error } = await query.order('nombre')
      if (error) throw error

      // Fetch categorias and proveedores for this tenant
      const [{ data: categorias }, { data: proveedores }] = await Promise.all([
        supabase.from('categorias_insumo').select('*').eq('tenant_id', tenantId!).order('orden'),
        supabase.from('proveedores').select('*').eq('tenant_id', tenantId!).eq('activo', true).order('nombre'),
      ])

      // Create lookup maps
      const catMap = new Map((categorias || []).map(c => [c.id, c]))
      const provMap = new Map((proveedores || []).map(p => [p.id, p]))

      // Merge relations into insumos
      return (insumos || []).map(i => ({
        ...i,
        categoria: i.categoria_id ? catMap.get(i.categoria_id) || null : null,
        proveedor: i.proveedor_id ? provMap.get(i.proveedor_id) || null : null,
      })) as Insumo[]
    },
  })
}

export function useInsumosStockBajo() {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['insumos-stock-bajo', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data: insumos, error } = await supabase
        .from('insumos')
        .select('*')
        .eq('tenant_id', tenantId!)
        .eq('activo', true)
      if (error) throw error

      // Fetch categorias for this tenant
      const { data: categorias } = await supabase
        .from('categorias_insumo')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('orden')

      const catMap = new Map((categorias || []).map(c => [c.id, c]))

      // Filter in memory and merge categoria
      return (insumos || [])
        .filter(i => i.stock_actual <= i.stock_minimo)
        .map(i => ({
          ...i,
          categoria: i.categoria_id ? catMap.get(i.categoria_id) || null : null,
        })) as Insumo[]
    },
  })
}

export function useCrearInsumo() {
  const qc = useQueryClient()
  const { tenantId } = useAuth()
  return useMutation({
    mutationFn: async (nuevo: InsumoInsert) => {
      const { data, error } = await supabase
        .from('insumos')
        .insert({ ...nuevo, tenant_id: tenantId! })
        .select()
        .single()
      if (error) throw error
      return data as Insumo
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['insumos'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

export function useActualizarInsumo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (i: InsumoUpdate & { id: string }) => {
      const { id, ...rest } = i
      const { error } = await supabase.from('insumos').update(rest).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['insumos'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

export function useAjustarStock() {
  const qc = useQueryClient()
  const { tenantId } = useAuth()
  return useMutation({
    mutationFn: async ({
      insumoId,
      cantidad,
      tipo,
      origen,
      notas,
    }: {
      insumoId: string
      cantidad: number
      tipo: 'entrada' | 'salida' | 'ajuste' | 'merma'
      origen: string
      notas?: string
    }) => {
      const { data, error } = await supabase.rpc('ajustar_stock', {
        p_insumo_id: insumoId,
        p_cantidad: cantidad,
        p_tipo: tipo,
        p_origen: origen,
        p_notas: notas,
        p_tenant_id: tenantId!,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['insumos'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

// Movimiento simple entrada/salida (usa RPC o insert directo)
export function useRegistrarMovimiento() {
  const qc = useQueryClient()
  const { tenantId } = useAuth()
  return useMutation({
    mutationFn: async (mov: MovimientoStockInsert) => {
      const { data, error } = await supabase
        .from('movimientos_stock')
        .insert({ ...mov, tenant_id: tenantId!, usuario_id: (await supabase.auth.getUser()).data.user?.id })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insumos'] })
      qc.invalidateQueries({ queryKey: ['movimientos_stock'] })
    },
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

// ===== Hooks: Movimientos Stock =====
export function useMovimientosStock(insumoId?: string) {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['movimientos_stock', tenantId, insumoId],
    enabled: !!tenantId,
    queryFn: async () => {
      let query = supabase
        .from('movimientos_stock')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('created_at', { ascending: false })
      if (insumoId) query = query.eq('insumo_id', insumoId)
      const { data: movimientos, error } = await query.limit(100)
      if (error) throw error

      // Fetch insumos for this tenant to get nombre/unidad
      const { data: insumos } = await supabase
        .from('insumos')
        .select('id, nombre, unidad')
        .eq('tenant_id', tenantId!)

      const insumoMap = new Map((insumos || []).map(i => [i.id, i]))

      return (movimientos || []).map(m => ({
        ...m,
        insumo: m.insumo_id ? insumoMap.get(m.insumo_id) || null : null,
      })) as MovimientoStock[]
    },
  })
}

// ===== Hooks: Órdenes de Compra =====
export function useOrdenesCompra() {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['ordenes_compra', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      // Fetch ordenes without embedded
      const { data: ordenes, error } = await supabase
        .from('ordenes_compra')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('created_at', { ascending: false })
      if (error) throw error

      // Fetch proveedores for this tenant
      const { data: proveedores } = await supabase
        .from('proveedores')
        .select('id, nombre')
        .eq('tenant_id', tenantId!)
        .eq('activo', true)

      const provMap = new Map((proveedores || []).map(p => [p.id, p]))

      // Fetch items for all ordenes
      const ordenIds = (ordenes || []).map(o => o.id)
      const itemsMap = new Map<string, any[]>()
      if (ordenIds.length > 0) {
        const { data: items } = await supabase
          .from('orden_compra_items')
          .select('*, insumo_id:insumos(nombre, unidad)')
          .in('orden_compra_id', ordenIds)
        // Note: insumo_id:insumos embedded might still fail, so fetch insumos separately
        if (items) {
          const insumoIds = [...new Set(items.map(i => i.insumo_id).filter(Boolean))]
          let insumoMap = new Map()
          if (insumoIds.length > 0) {
            const { data: insumos } = await supabase
              .from('insumos')
              .select('id, nombre, unidad')
              .eq('tenant_id', tenantId!)
              .in('id', insumoIds)
            insumoMap = new Map((insumos || []).map(i => [i.id, i]))
          }
          // Merge insumo into items
          items.forEach(item => {
            const insumo = insumoMap.get(item.insumo_id)
            if (insumo) {
              item.insumo = { nombre: insumo.nombre, unidad: insumo.unidad }
            }
            if (!itemsMap.has(item.orden_compra_id)) {
              itemsMap.set(item.orden_compra_id, [])
            }
            itemsMap.get(item.orden_compra_id)!.push(item)
          })
        }
      }

      return (ordenes || []).map(o => ({
        ...o,
        proveedor: o.proveedor_id ? provMap.get(o.proveedor_id) || null : null,
        items: itemsMap.get(o.id) || [],
      })) as OrdenCompra[]
    },
  })
}

export function useCrearOrdenCompra() {
  const qc = useQueryClient()
  const { tenantId } = useAuth()
  return useMutation({
    mutationFn: async (nueva: OrdenCompraInsert) => {
      const { data, error } = await supabase
        .from('ordenes_compra')
        .insert({ ...nueva, tenant_id: tenantId!, created_by: (await supabase.auth.getUser()).data.user?.id })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ordenes_compra'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

export function useActualizarOrdenCompra() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (oc: OrdenCompraUpdate & { id: string }) => {
      const { id, ...rest } = oc
      const { error } = await supabase.from('ordenes_compra').update(rest).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ordenes_compra'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

export function useRecibirOrdenCompra() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      ordenId,
      items,
    }: {
      ordenId: string
      items: { itemId: string; cantidad_recibida: number; costo_unitario_real?: number }[]
    }) => {
      // Actualizar items recibidos
      for (const item of items) {
        const { error } = await supabase
          .from('orden_compra_items')
          .update({
            cantidad_recibida: item.cantidad_recibida,
            costo_unitario_real: item.costo_unitario_real,
          })
          .eq('id', item.itemId)
        if (error) throw error
      }

      // Verificar si todos los items están recibidos completamente
      const { data: allItems } = await supabase
        .from('orden_compra_items')
        .select('cantidad_solicitada, cantidad_recibida')
        .eq('orden_compra_id', ordenId)

      const todosRecibidos = allItems?.every(i => i.cantidad_recibida >= i.cantidad_solicitada) ?? false

      // Actualizar estado de la orden
      const { error: errorOC } = await supabase
        .from('ordenes_compra')
        .update({
          estado: todosRecibidos ? 'recibida' : 'recibida_parcial',
          fecha_recibida: todosRecibidos ? new Date().toISOString() : null,
        })
        .eq('id', ordenId)
      if (errorOC) throw errorOC
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ordenes_compra'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

// ===== Hooks: Índice Inflación =====
export function useIndiceInflacion() {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['indice_inflacion', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('indice_inflacion')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('anio', { ascending: false })
        .order('mes', { ascending: false })
      if (error) throw error
      return data as IndiceInflacion[]
    },
  })
}

export function useUpsertIndiceInflacion() {
  const qc = useQueryClient()
  const { tenantId } = useAuth()
  return useMutation({
    mutationFn: async (indice: IndiceInflacionInsert) => {
      const { data, error } = await supabase
        .from('indice_inflacion')
        .upsert({ ...indice, tenant_id: tenantId! }, { onConflict: 'tenant_id,anio,mes' })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['indice_inflacion'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

// ===== Hooks: Receta Servicio-Insumo =====
export function useRecetaServicio(servicioId?: string) {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['servicio_insumo', tenantId, servicioId],
    enabled: !!tenantId && !!servicioId,
    queryFn: async () => {
      const { data: receta, error } = await supabase
        .from('servicio_insumo')
        .select('*')
        .eq('servicio_id', servicioId!)
        .eq('tenant_id', tenantId!)
      if (error) throw error

      // Fetch insumos for this tenant
      const insumoIds = (receta || []).map(r => r.insumo_id).filter(Boolean)
      let insumoMap = new Map()
      if (insumoIds.length > 0) {
        const { data: insumos } = await supabase
          .from('insumos')
          .select('id, nombre, unidad, costo_promedio')
          .eq('tenant_id', tenantId!)
          .in('id', insumoIds)
        insumoMap = new Map((insumos || []).map(i => [i.id, i]))
      }

      return (receta || []).map(r => ({
        ...r,
        insumo: r.insumo_id ? insumoMap.get(r.insumo_id) || null : null,
      })) as ServicioInsumo[]
    },
  })
}

export function useGestionarRecetaServicio() {
  const qc = useQueryClient()
  const { tenantId } = useAuth()
  return useMutation({
    mutationFn: async ({
      servicioId,
      insumos,
    }: {
      servicioId: string
      insumos: { insumo_id: string; cantidad: number }[]
    }) => {
      // Borrar receta actual
      const { error: delError } = await supabase
        .from('servicio_insumo')
        .delete()
        .eq('servicio_id', servicioId)
        .eq('tenant_id', tenantId!)
      if (delError) throw delError

      // Insertar nueva receta
      if (insumos.length > 0) {
        const { error: insError } = await supabase
          .from('servicio_insumo')
          .insert(
            insumos.map(i => ({
              tenant_id: tenantId!,
              servicio_id: servicioId,
              insumo_id: i.insumo_id,
              cantidad: i.cantidad,
            }))
          )
        if (insError) throw insError
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['servicio_insumo'] }),
    onError: (e) => toast.error(traducirError(e, 'database')),
  })
}

// ===== Hooks: Reportes =====
export function useGastoMensualCategoria(anio: number, mes: number) {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['gasto-mensual-categoria', tenantId, anio, mes],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('gasto_mensual_categoria', {
        p_tenant_id: tenantId!,
        p_anio: anio,
        p_mes: mes,
      })
      if (error) throw error
      return data as GastoMensualCategoria[]
    },
  })
}

// Helper para calcular inflación acumulada hasta una fecha
export function useInflacionAcumulada(hastaAnio: number, hastaMes: number) {
  const { tenantId } = useAuth()
  return useQuery({
    queryKey: ['inflacion-acumulada', tenantId, hastaAnio, hastaMes],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('indice_inflacion')
        .select('valor')
        .eq('tenant_id', tenantId!)
        .lte('anio', hastaAnio)
        .filter('mes', 'lte', hastaMes)
      if (error) throw error
      const acum = (data as IndiceInflacion[]).reduce((sum, d) => sum + d.valor, 0)
      return acum
    },
  })
}

// ===== Multi-local: obtener todos los locales del usuario =====
export interface MiembroTenant {
  tenant_id: string
  rol: string
  tenant_nombre: string
  tenant_slug: string
}

export function useMisLocales() {
  const { session } = useAuth()
  return useQuery({
    queryKey: ['mis-locales', session?.user?.id],
    enabled: !!session?.user?.id,
    queryFn: async () => {
      // 1) Obtener membresías del usuario
      const { data: membresias, error: errM } = await supabase
        .from('miembros')
        .select('tenant_id, rol')
        .eq('user_id', session!.user.id)
      if (errM) throw errM

      if (!membresias?.length) return [] as MiembroTenant[]

      // 2) Obtener info de tenants (nombres, slugs)
      const tenantIds = membresias.map(m => m.tenant_id)
      const { data: tenants, error: errT } = await supabase
        .from('tenants')
        .select('id, nombre, slug')
        .in('id', tenantIds)
      if (errT) throw errT

      const tenantMap = new Map((tenants || []).map(t => [t.id, t]))

      // 3) Combinar en memoria
      return membresias.map(m => {
        const t = tenantMap.get(m.tenant_id)
        return {
          tenant_id: m.tenant_id,
          rol: m.rol,
          tenant_nombre: t?.nombre ?? '',
          tenant_slug: t?.slug ?? '',
        }
      }) as MiembroTenant[]
    },
  })
}

// ===== Exportaciones CSV =====
export function exportarInsumosCSV(insumos: Insumo[]) {
  const headers = ['Nombre', 'Categoría', 'Unidad', 'Stock Actual', 'Stock Mínimo', 'Costo Unitario', 'Costo Promedio', 'Proveedor', 'Activo']
  const rows = insumos.map(i => [
    i.nombre,
    i.categoria?.nombre || '',
    i.unidad,
    i.stock_actual.toString(),
    i.stock_minimo.toString(),
    i.costo_unitario.toFixed(2),
    i.costo_promedio.toFixed(2),
    i.proveedor?.nombre || '',
    i.activo ? 'Sí' : 'No',
  ])
  return [headers, ...rows].map(r => r.join(',')).join('\n')
}

export function exportarMovimientosCSV(movimientos: MovimientoStock[]) {
  const headers = ['Fecha', 'Insumo', 'Tipo', 'Cantidad', 'Costo Unit.', 'Origen', 'Notas']
  const rows = movimientos.map(m => [
    new Date(m.created_at).toLocaleDateString('es-AR'),
    m.insumo?.nombre || '',
    m.tipo,
    m.cantidad.toString(),
    m.costo_unitario?.toFixed(2) || '',
    m.origen,
    m.notas || '',
  ])
  return [headers, ...rows].map(r => r.join(',')).join('\n')
}

export function exportarGastosMensualesCSV(gastos: GastoMensualCategoria[]) {
  const headers = ['Categoría', 'Gasto Nominal (ARS)', 'Gasto Real (ARS)', 'Inflación Acum. (%)']
  const rows = gastos.map(g => [
    g.categoria_nombre,
    g.gasto_nominal.toFixed(2),
    g.gasto_real.toFixed(2),
    (g.inflacion_acum * 100).toFixed(2),
  ])
  return [headers, ...rows].map(r => r.join(',')).join('\n')
}